/*!
// <copyright file="ListEditManager.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/// <reference path="../html-binding-controls.js" />
/// <reference path="../html-binding-handlers.ts" />
/// <reference path="Grid.js" />

(function () {
    'use strict';

    /**
     * @class EditManager
     * @classdesc The EditManager component of the List control
     * @summary Responsible for listening to change events from input controls and passing control state to SyncManager
     * @internal
     * @memberof $dyn.controls.List
     * @param {Object} options - Object having references to other components like SyncManager and Renderer
     */
    var EditManager = function (options) {
        this.element = options.element;
        this._syncManager = options.syncManager;
        this._renderer = options.renderer;
        this._getSelectedRow = options.getSelectedRow;
        this._allowEdit = options.allowEdit;

        this._edits = {};
        if (this.element) {
            this._changeEventListener = this._onCellEdit.bind(this);
            this._readOnlyEventListener = this._onCellReadOnlyEvent.bind(this);
            this.element.addEventListener(this._changeEvent, this._changeEventListener);
            this.element.addEventListener(this._readOnlyEvent, this._readOnlyEventListener);
        }
    };

    EditManager.prototype = {
        element: undefined,
        _syncManager: undefined,
        _renderer: undefined,
        _selectedRow: undefined,
        _allowEdit: undefined,

        _changeEvent: $dyn.ui.ChangeEvent,
        _readOnlyEvent: $dyn.ui.ReadOnlyEvent,

        /**
        * @function
        * @memberof $dyn.controls.List.EditManager#
        * @private
        * @param {Object} rowElement - The row element to get the row id of
        * @returns {Integer} The internal row id
        * @summary Calls the Renderer to get the row id for the row element
        */
        _getRowIdByElement: function (rowElement) {
            return this._renderer.getInternalRowIdByElement(rowElement);
        },

        _edits: undefined,

        /**
        * @function
        * @memberof $dyn.controls.List.EditManager#
        * @private
        * @param {String} columnName - The column name in the grid
        * @returns {Object} The active control for this column
        * @summary Gets the active control of the specified column, from the user selected row
        */
        _getActiveControl: function (columnName) {
            if (this._getSelectedRow()) {
                var activeControlElement = $dyn.util.filter(this._getSelectedRow().children, $dyn.ui.selector().attr('data-dyn-controlname', columnName))[0];
                if (activeControlElement) {
                    return $dyn.context(activeControlElement);
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.EditManager#
        * @private
        * @param {Object} element - The list cell element
        * @returns {Object} cell item
        * @summary Gets the cell item for the given element
        */
        _getCell: function (element) {
            var cellElement = $(element).closest('.listCell')[0];
            var rowElement = $(cellElement).closest('.listItem')[0];
            var control = $dyn.context(cellElement);
            var rowId = this._getRowIdByElement(rowElement);

            if (cellElement && control && control.GetState && rowElement) {
                var columnName = cellElement.getAttribute('data-dyn-controlname');
                if (columnName) {
                    return {
                        rowId: rowId,
                        columnName: columnName,
                        cellControl: control,
                        rowElement: rowElement,
                    };
                }
            }
        },

        _onCellReadOnlyEvent: function (evt) {
            var cellInfo = this._getCell(evt.target);
            if (cellInfo) {
                var rowId = cellInfo.rowId;
                var columnName = cellInfo.columnName;
                var control = cellInfo.cellControl;
                var rowElement = cellInfo.rowElement;
                var state = null;
                if (control && control.GetState) {
                    state = control.GetState();
                }

                if (state) {
                    var commitAction = function () {
                        var activeControl = this._getActiveControl(columnName);
                        if (activeControl && activeControl.ApplyState) {
                            activeControl.ApplyState(state);
                        }
                        if (control && control.ResetState) {
                            control.ResetState();
                        }
                    };
                    this._syncManager.queueCellEdit(
                        rowId,
                        columnName,
                        state,
                        commitAction.bind(this),
                        function cancelAction() {
                            if (control && control.ResetState) {
                                control.ResetState();
                            }
                        });
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.EditManager#
        * @private
        * @param {Object} evt - A cell change event
        * @summary Handles a cell change event by creating cell edit actions and passing them to the Sync Manager
        */
        _onCellEdit: function (evt) {
            var cellInfo = this._getCell(evt.target);
            if (cellInfo) {
                var rowId = cellInfo.rowId,
                    columnName = cellInfo.columnName,
                    control = cellInfo.cellControl,
                    rowElement = cellInfo.rowElement,
                    state = control.GetState();

                if (state && $dyn.value(control.AllowEdit)) {
                    var editsForRow = this._edits[rowId];
                    if (!editsForRow) {
                        editsForRow = this._edits[rowId] = {};
                    }
                    control.validating = true;

                    editsForRow[columnName] = state;
                    var self = this;
                    var commitAction = function () {
                        var activeControl = this._getActiveControl(columnName);
                        var previousState = activeControl && activeControl.GetState(state);
                        var result = activeControl && activeControl.ApplyState(state);

                        if (result) {
                            result.done(function (revertValue) {
                                if (revertValue) {
                                    control.ApplyState(previousState, true);
                                    activeControl.ApplyState(previousState, true);
                                }
                            }).always(function () {
                                control.validating = false;

                                if (editsForRow[columnName] === state) {
                                    delete editsForRow[columnName];
                                    if (Object.keys(editsForRow).length === 0) {
                                        delete self._edits[rowId];
                                    }
                                }
                            });
                        }

                        return result;
                    };

                    this._syncManager.queueCellEdit(
                        rowId,
                        columnName,
                        state,
                        commitAction.bind(this),
                        function cancelAction() {
                            control.validating = false;
                        });
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.EditManager#
        * @public
        * @summary Cleans up the change event listener
        */
        dispose: function () {
            if (this.element) {
                if (this._changeEventListener) {
                    this.element.removeEventListener(this._changeEvent, this._changeEventListener);
                    this._changeEventListener = null;
                }
                if (this._readOnlyEventListener) {
                    this.element.removeEventListener(this._readOnlyEvent, this._readOnlyEventListener);
                    this._readOnlyEventListener = null;
                }
            }
        }
    };

    $dyn.controls.List.EditManager = EditManager;

})();